import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { subjectColor } from "@/lib/constants";
import ExamTypeToggle from "@/components/ExamTypeToggle";

const SUBJECT_TEMPLATES = [
  { label: "Tüm Dersler", keys: ["turkce", "matematik", "fen", "sosyal", "ingilizce", "din"] },
  { label: "Sadece Matematik", keys: ["matematik"] },
  { label: "Sadece Türkçe", keys: ["turkce"] },
  { label: "Sadece Fen", keys: ["fen"] },
  { label: "Sayısal (Mat + Fen)", keys: ["matematik", "fen"] },
  { label: "Sözel (Türkçe + Sosyal + Din)", keys: ["turkce", "sosyal", "din"] },
  { label: "Sadece İngilizce", keys: ["ingilizce"] },
];

export default function CreateExam() {
  const nav = useNavigate();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ name: "", exam_no: "", exam_type: "KTT", date: "", grade: "8", duration: 90, description: "" });
  const [subjects, setSubjects] = useState([]);
  const [enabled, setEnabled] = useState({});
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState([]);
  const [perGrade, setPerGrade] = useState(false);
  const [gradeConfigs, setGradeConfigs] = useState({});

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setSubjects(r.data.subjects.map((s) => ({ ...s })));
      const en = {};
      r.data.subjects.forEach((s) => { en[s.key] = true; });
      setEnabled(en);
      if (r.data.use_default_grade_configs && r.data.default_grade_configs) {
        setPerGrade(true);
        const gc = {};
        Object.entries(r.data.default_grade_configs).forEach(([g, subs]) => { gc[g] = subs.map((s) => ({ ...s })); });
        setGradeConfigs(gc);
        toast.info("Ayarlardaki seviyeye göre varsayılan soru dağılımı uygulandı.", { duration: 3500 });
      }
    });
    api.get("/classes").then((r) => {
      const gs = [...new Set(r.data.map((c) => c.grade))].sort();
      setGrades(gs);
    }).catch(() => {});
  }, []);

  const total = subjects.reduce((a, s) => a + (enabled[s.key] !== false ? (parseInt(s.question_count) || 0) : 0), 0);
  const isOn = (key) => enabled[key] !== false;
  const activeSubjects = subjects.filter((s) => isOn(s.key));
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCount = (i, v) => setSubjects((ss) => ss.map((s, idx) => idx === i ? { ...s, question_count: v } : s));
  const toggleSubject = (key) => setEnabled((prev) => ({ ...prev, [key]: prev[key] === false }));
  const applyTemplate = (keys) => {
    const en = {};
    subjects.forEach((s) => { en[s.key] = keys.includes(s.key); });
    setEnabled(en);
  };
  const templateKeys = (tpl) => tpl.keys.filter((k) => subjects.some((s) => s.key === k));
  const activeKeys = activeSubjects.map((s) => s.key).sort().join(",");
  const isTemplateActive = (tpl) => templateKeys(tpl).slice().sort().join(",") === activeKeys;
  const gradeTotal = (g) => (gradeConfigs[g] || subjects).filter((s) => isOn(s.key)).reduce((a, s) => a + (parseInt(s.question_count) || 0), 0);
  const setGradeCount = (g, i, v) => setGradeConfigs((prev) => {
    const cur = (prev[g] || subjects).map((s, idx) => idx === i ? { ...s, question_count: v } : s);
    return { ...prev, [g]: cur };
  });

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Sınav adı gerekli"); return; }
    if (total === 0) { toast.error("Toplam soru sayısı 0 olamaz"); return; }
    setSaving(true);
    try {
      const chosen = activeSubjects.map((s) => ({ ...s, question_count: parseInt(s.question_count) || 0 })).filter((s) => s.question_count > 0);
      if (!chosen.length) { toast.error("En az bir derse soru sayısı girmelisiniz"); setSaving(false); return; }
      const payload = { ...form, duration: parseInt(form.duration) || 90, subjects: chosen };
      if (perGrade && grades.length) {
        payload.grade_configs = {};
        grades.forEach((g) => { payload.grade_configs[g] = (gradeConfigs[g] || subjects).filter((s) => isOn(s.key)).map((s) => ({ ...s, question_count: parseInt(s.question_count) || 0 })).filter((s) => s.question_count > 0); });
      }
      const { data } = await api.post("/exams", payload);
      toast.success("Sınav oluşturuldu. Şimdi cevap anahtarını ve konuları (müfredat) girebilirsiniz.");
      nav(`/sinav/${data.id}/cevap-anahtari`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-muted-foreground">Yükleniyor...</div>;

  return (
    <div>
      <PageHeader title="Sınav Oluştur" desc="Yeni bir KTT sınavı tanımlayın. Soru sayıları değiştirilebilir." />
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 bg-card border-border space-y-5">
          <h3 className="font-semibold">Sınav Bilgileri</h3>
          <div className="space-y-2">
            <Label>Sınav Türü *</Label>
            <ExamTypeToggle value={form.exam_type} onChange={(v) => set("exam_type", v)} tid="create-exam-type" />
            <p className="text-xs text-muted-foreground">Haftalık KTT ve Deneme sonuçları/ortalamaları birbirinden ayrı değerlendirilir.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2 sm:col-span-2">
              <Label>Sınav Adı *</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="KTT - 1" data-testid="exam-name" />
            </div>
            <div className="space-y-2">
              <Label>Sınav Numarası</Label>
              <Input value={form.exam_no} onChange={(e) => set("exam_no", e.target.value)} placeholder="1" data-testid="exam-no" />
            </div>
            <div className="space-y-2">
              <Label>Sınav Tarihi</Label>
              <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="exam-date" />
            </div>
            <div className="space-y-2">
              <Label>Sınıf Seviyesi</Label>
              <Input value={form.grade} onChange={(e) => set("grade", e.target.value)} placeholder="8" data-testid="exam-grade" />
            </div>
            <div className="space-y-2">
              <Label>Süre (dakika)</Label>
              <Input type="number" value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="exam-duration" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Açıklama</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} data-testid="exam-desc" />
            </div>
          </div>

          <div className="pt-2">
            <h3 className="font-semibold mb-1">Dersler & Soru Dağılımı</h3>
            <p className="text-xs text-muted-foreground mb-3">Sınavda yer almasını istediğiniz dersleri açık bırakın. Kapattığınız dersler optik formda ve sonuçlarda <strong>çıkmaz</strong>. Hızlı başlamak için bir şablon seçebilirsiniz.</p>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="subject-templates">
              {SUBJECT_TEMPLATES.filter((tpl) => templateKeys(tpl).length > 0).map((tpl) => {
                const active = isTemplateActive(tpl);
                return (
                  <button
                    key={tpl.label}
                    type="button"
                    onClick={() => applyTemplate(tpl.keys)}
                    data-testid={`subject-template-${tpl.keys.join("-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:border-primary hover:text-primary"}`}
                  >
                    {tpl.label}
                  </button>
                );
              })}
            </div>
            <div className="space-y-2">
              {subjects.map((s, i) => {
                const c = subjectColor(s.key);
                const on = isOn(s.key);
                return (
                  <div key={s.key} className={`flex items-center gap-3 p-3 rounded-lg border bg-background transition-opacity ${on ? "border-border" : "border-dashed border-border opacity-50"}`}>
                    <Switch checked={on} onCheckedChange={() => toggleSubject(s.key)} data-testid={`subject-enabled-${s.key}`} />
                    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${c.badge}`}>{s.name}</span>
                    <Input type="number" min="0" value={s.question_count} disabled={!on} onChange={(e) => setCount(i, e.target.value)}
                      className="w-24 ml-auto font-mono" data-testid={`subject-count-${s.key}`} />
                    <span className="text-xs text-muted-foreground w-10">soru</span>
                  </div>
                );
              })}
            </div>
          </div>

          {grades.length > 0 && (
            <div className="pt-2 border-t border-border">
              <div className="flex items-center gap-2 mb-3">
                <Switch checked={perGrade} onCheckedChange={setPerGrade} data-testid="per-grade-toggle" />
                <div>
                  <Label className="text-sm font-semibold">Sınıf seviyesine göre farklı soru sayıları</Label>
                  <p className="text-xs text-muted-foreground">Örn: 5. sınıflara 60, 8. sınıflara 90 soru. Kapalıysa herkes varsayılan yapıyı alır.</p>
                </div>
              </div>
              {perGrade && grades.map((g) => (
                <div key={g} className="mb-4 rounded-lg border border-border p-3 bg-background">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-bold">{g}. Sınıflar</span>
                    <span className="text-xs font-mono text-muted-foreground" data-testid={`grade-total-${g}`}>Toplam: {gradeTotal(g)}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-2">Varsayılandan kopyalandı; bu seviye için düzenleyebilirsiniz.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(gradeConfigs[g] || subjects).filter((s) => isOn(s.key)).map((s) => {
                      const i = (gradeConfigs[g] || subjects).findIndex((x) => x.key === s.key);
                      return (
                      <div key={s.key} className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground flex-1 truncate">{s.name}</span>
                        <Input type="number" min="0" value={s.question_count} onChange={(e) => setGradeCount(g, i, e.target.value)}
                          className="w-16 h-8 font-mono text-xs" data-testid={`grade-${g}-count-${s.key}`} />
                      </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <Card className="p-6 bg-primary text-primary-foreground">
            <div className="text-xs font-semibold uppercase tracking-wider opacity-80">Toplam Soru</div>
            <div className="text-5xl font-extrabold mt-2 font-mono" data-testid="total-questions">{total}</div>
            <div className="text-sm opacity-80 mt-1">{activeSubjects.length} ders · {form.grade}. sınıf</div>
          </Card>
          <Card className="p-6 bg-card border-border space-y-3">
            <p className="text-sm text-muted-foreground">Sınavı oluşturduktan sonra cevap anahtarı girişine yönlendirileceksiniz.</p>
            <Button className="w-full" onClick={submit} disabled={saving} data-testid="exam-submit">
              {saving ? "Oluşturuluyor..." : "Sınavı Oluştur"}
            </Button>
          </Card>
        </div>
      </div>
    </div>
  );
}
